/**
 * One filter, two ways in: the busiest options as pills next to "All", and a searchable dropdown
 * holding every option (grouped under its parent when the options form a tree), with a check on
 * the one in effect. `top={0}` drops the pills and leaves just the dropdown, for short lists.
 */

import { useMemo, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator
} from '@/components/ui/command';

export interface FilterOption {
  label: string;
  /** Omitted for an option that only groups its children and cannot be picked itself. */
  value?: string;
  count?: number;
  /** Ranks the option for the pills when its count is not shown; defaults to the count. */
  weight?: number;
  children?: FilterOption[];
}

interface FilterMenuProps {
  /** What is being filtered, e.g. "Category"; names the dropdown and its search box. */
  label: string;
  options: FilterOption[];
  /** The picked option's value; undefined means All. */
  value?: string;
  onChange: (value: string | undefined) => void;
  /** How many of the busiest pickable options show as pills. */
  top?: number;
  /** Shown on the All pill / item. */
  total?: number;
}

const PILL =
  'inline-flex h-7 shrink-0 cursor-pointer items-center gap-1 rounded-full border px-3 text-xs font-medium whitespace-nowrap transition-colors';
const PILL_ON = 'border-gray-500 bg-white text-gray-900 shadow-sm';
const PILL_OFF = 'border-transparent text-gray-800 hover:bg-gray-100';

const withCount = (label: string, count?: number) => (count === undefined ? label : `${label} (${count})`);

/** Every option that can be picked, with the label it shows once picked. */
function pickable(options: FilterOption[], parent?: string): { value: string; label: string; short: string; count?: number; weight: number }[] {
  return options.flatMap((o) => [
    ...(o.value
      ? [{ value: o.value, label: parent ? `${parent} · ${o.label}` : o.label, short: o.label, count: o.count, weight: o.weight ?? o.count ?? 0 }]
      : []),
    ...pickable(o.children ?? [], o.label)
  ]);
}

export function FilterMenu({ label, options, value, onChange, top = 3, total }: FilterMenuProps) {
  const [open, setOpen] = useState(false);
  const all = useMemo(() => pickable(options), [options]);
  const picked = all.find((o) => o.value === value);
  // A pickable parent that is picked, or whose child is: its children get a row of their own.
  const parent = options.find(
    (o) => o.value && o.children?.length && (o.value === value || o.children.some((c) => c.value === value))
  );
  const anchor = parent ? all.find((o) => o.value === parent.value) : picked;

  // The busiest options, with the picked one (or its parent) kept on screen even when it is not among them.
  const pills = useMemo(() => {
    if (top === 0) return [];
    const busiest = [...all].sort((a, b) => b.weight - a.weight).slice(0, top);
    return anchor && !busiest.includes(anchor) ? [...busiest.slice(0, top - 1), anchor] : busiest;
  }, [all, anchor, top]);

  const pick = (next: string | undefined) => {
    onChange(next);
    setOpen(false);
  };

  const ALL_ITEM = { label: 'All', count: total };
  const item = (o: { value?: string; label: string; count?: number }, key: string) => (
    <CommandItem key={key} value={key} disabled={!o.value && o !== ALL_ITEM} onSelect={() => pick(o.value)} className='text-xs'>
      <Check className={cn('size-3.5', o.value === value ? 'opacity-100' : 'opacity-0')} />
      <span className='flex-1 truncate'>{o.label}</span>
      {o.count !== undefined && <span className='text-gray-700'>{o.count}</span>}
    </CommandItem>
  );
  const showPickedOnTrigger = picked && !pills.includes(picked) && !parent;
  const pill = (o: { value?: string; label: string; short?: string; count?: number }, active: boolean, small = false) => (
    <button
      key={o.value ?? '__all'}
      type='button'
      aria-pressed={active}
      title={o.short && o.short !== o.label ? o.label : undefined}
      onClick={() => onChange(o.value)}
      className={cn(PILL, small && 'h-6 px-2.5 text-[11px]', active ? PILL_ON : PILL_OFF)}
    >
      {withCount(o.short ?? o.label, o.count)}
    </button>
  );

  return (
    <div className='flex min-w-0 flex-col gap-1.5'>
      <div className='flex min-w-0 items-center gap-1.5'>
        {top > 0 && (
          <>
            {pill({ label: 'All', count: total }, !value)}
            {pills.map((o) => pill(o, o.value === value || o === anchor))}
          </>
        )}
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button
              type='button'
              className={cn(
                PILL,
                top === 0 || showPickedOnTrigger ? (value ? PILL_ON : 'border-gray-500 bg-white text-gray-800') : PILL_OFF
              )}
            >
              {top === 0
                ? `${label}: ${picked?.label ?? 'All'}`
                : showPickedOnTrigger
                  ? picked.label
                  : `All ${label.toLowerCase()}`}
              <ChevronDown className='size-3.5 text-gray-700' />
            </button>
          </PopoverTrigger>
          <PopoverContent align='start' className='w-72 p-0'>
            <Command>
              {all.length > 8 && <CommandInput placeholder={`Search ${label.toLowerCase()}…`} className='text-xs' />}
              <CommandList className='max-h-80'>
                <CommandEmpty className='py-6 text-center text-xs text-gray-700'>Nothing matches.</CommandEmpty>
                <CommandGroup>{item(ALL_ITEM, '__all')}</CommandGroup>
                <CommandSeparator />
                {options
                  .filter((o) => o.children?.length)
                  .map((o) => (
                    <CommandGroup key={o.label} heading={o.label}>
                      {o.value && item({ ...o, label: `All in ${o.label}` }, `${o.label}::all`)}
                      {o.children!.map((c) => item(c, `${o.label}::${c.label}`))}
                    </CommandGroup>
                  ))}
                {options.some((o) => !o.children?.length) && (
                  <CommandGroup>{options.filter((o) => !o.children?.length).map((o) => item(o, o.label))}</CommandGroup>
                )}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>
      {top > 0 && parent && (
        <div className='flex min-w-0 items-center gap-1.5 overflow-x-auto pl-1'>
          {pill({ label: 'All', value: parent.value }, value === parent.value, true)}
          {parent.children!.map((c) => pill(c, c.value === value, true))}
        </div>
      )}
    </div>
  );
}
